// ============================================================
// TMR Portal Admin — os modelos que o v1.1 tem e ainda não estão aqui, e o botão que os traz
// ============================================================
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import {
  isNotConfigured,
  legacyScopeLabel,
  legacyTemplatesMissingHere,
} from '../../data/contractTemplates/status';
import type { LegacyImportItem, LegacyImportReport } from '../../data/contractTemplates/contract';

/** Props for {@link LegacyImportNotice}. */
interface LegacyImportNoticeProps {
  /** The initial load's preview. */
  preview: UseQueryResult<LegacyImportReport>;
  /** The load itself; its last answer is what the notice reports after a run. */
  importing: UseMutationResult<LegacyImportReport, Error, void>;
  canManage: boolean;
}

/**
 * The templates the v1.1 holds that are not here yet — and the one action that fixes it (`24` P2b).
 *
 * **It says nothing when there is nothing to say**: every template already here, or an environment with no
 * legacy connection at all (a 503, which is an answer rather than a fault). When something is missing it
 * lists each template with where it applies and — for the ones the load could not bring — why, because
 * each reason names what has to happen first.
 *
 * Until they are brought, those templates keep being issued over there and cannot be edited here, and
 * creating another one for the same slot would be refused — which is why this sits above everything else
 * on the screen.
 */
export function LegacyImportNotice({ preview, importing, canManage }: LegacyImportNoticeProps) {
  if (preview.isError) {
    if (isNotConfigured(preview.error)) return null;

    return (
      <Notice tone="neutral">
        Não foi possível conferir se o v1.1 tem modelos que ainda não estão aqui.{' '}
        <button type="button" className="pa-link-btn" style={{ font: 'inherit' }} onClick={() => void preview.refetch()}>
          Tentar de novo
        </button>
      </Notice>
    );
  }

  const missing = legacyTemplatesMissingHere(preview.data);
  const waiting = missing.filter((item) => item.outcome === 'Pending');
  const brought = (importing.data?.items ?? []).filter((item) => item.outcome === 'Imported');

  return (
    <>
      {brought.length > 0 && (
        <Notice tone="success">
          {brought.length === 1 ? '1 modelo trazido do v1.1' : `${brought.length} modelos trazidos do v1.1`}, com
          as versões, os textos e os autores que tinham lá.
        </Notice>
      )}

      {importing.error != null && (
        <Notice tone="danger">
          {importing.error instanceof ApiError
            ? importing.error.detail
            : 'Não foi possível trazer os modelos do v1.1.'}
        </Notice>
      )}

      {missing.length > 0 && (
        <Notice tone="warning">
          <strong>
            {missing.length === 1
              ? 'O v1.1 tem 1 modelo que ainda não está aqui.'
              : `O v1.1 tem ${missing.length} modelos que ainda não estão aqui.`}
          </strong>{' '}
          {missing.length === 1
            ? 'Enquanto não for trazido, ele continua valendo do lado de lá e não pode ser editado aqui'
            : 'Enquanto não forem trazidos, eles continuam valendo do lado de lá e não podem ser editados aqui'}
          {' '}— e criar outro para o mesmo tipo e alcance seria recusado.
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {missing.map((item) => (
              <MissingItem key={item.templateId} item={item} />
            ))}
          </ul>
          {canManage && waiting.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Btn
                variant="primary"
                size="sm"
                disabled={importing.isPending}
                onClick={() => importing.mutate()}
              >
                {importing.isPending ? 'Trazendo…' : 'Importar do v1.1'}
              </Btn>
            </div>
          )}
        </Notice>
      )}
    </>
  );
}

function MissingItem({ item }: { item: LegacyImportItem }) {
  return (
    <li>
      <strong>{item.name}</strong> · {legacyScopeLabel(item)}
      {item.active ? '' : ' · arquivado'}
      {item.outcome === 'NotImported' && item.reason && (
        <div style={{ fontSize: 12.5, marginTop: 2 }}>Não dá para trazer ainda: {item.reason}</div>
      )}
    </li>
  );
}
